

<template>
  <el-dialog
    v-model="visible"
    title="密码找回"
    width="min(688px, calc(100vw - 32px))"
    class="enterprise-forgot-password-dialog"
    :append-to-body="true"
    :close-on-click-modal="false"
    @closed="handleClosed"
  >
    <el-form
      ref="forgotFormRef"
      :model="forgotForm"
      :rules="forgotRules"
      label-position="top"
      class="forgot-password-form"
    >
      <el-form-item
        label="主体账号"
        prop="account"
      >
        <el-input
          v-model="forgotForm.account"
          placeholder="请输入主体账号"
          clearable
        />
      </el-form-item>

      <el-form-item
        label="主体联系人电话"
        prop="phoneNumber"
      >
        <PhoneInput
          v-model="forgotForm.phoneNumber"
          placeholder="请输入主体联系人电话"
          class="forgot-phone-input"
          @keyup.enter="handleGetSmsCode"
        >
          <template #append>
            <el-button
              class="sms-code-btn"
              :disabled="smsCodeCountdown > 0"
              :loading="smsCodeLoading"
              @click="handleGetSmsCode"
            >
              {{ smsCodeButtonText }}
            </el-button>
          </template>
        </PhoneInput>
      </el-form-item>

      <el-form-item
        label="验证码"
        prop="smsCode"
      >
        <el-input
          v-model="forgotForm.smsCode"
          placeholder="请输入验证码"
          clearable
          maxlength="6"
          @keyup.enter="handleConfirmRetrieve"
        />
      </el-form-item>

      <p class="retrieve-password-tip">
        提交后，系统自动以邮件方式发送当前主体账号新密码，请注意查收，妥善保管。
      </p>

      <el-button
        type="primary"
        class="confirm-retrieve-btn"
        :loading="submitLoading"
        @click="handleConfirmRetrieve"
      >
        确认找回
      </el-button>
    </el-form>
  </el-dialog>
</template>


<script setup>
import { computed, nextTick, reactive, shallowRef, watch } from 'vue'
import { ElMessage } from 'element-plus'
import { getSmsCaptcha } from '@/api/utils/captcha'
import { retrieveEnterprisePassword } from '@/api/login'
import PhoneInput from '@/components/PhoneInput/index.vue'
import { GLOBAL_PHONE_PATTERN } from '@/utils/idValidator'

const COUNTDOWN_TIME = 60

const props = defineProps({
  modelValue: {
    type: Boolean,
    default: false
  },
  initialAccount: {
    type: String,
    default: ''
  }
})

const emit = defineEmits(['update:modelValue', 'success'])

const visible = computed({
  get: () => props.modelValue,
  set: value => emit('update:modelValue', value)
})

const forgotFormRef = shallowRef(null)
const smsCodeLoading = shallowRef(false)
const submitLoading = shallowRef(false)
const smsCodeCountdown = shallowRef(0)
const smsCodeTimer = shallowRef(null)

const forgotForm = reactive({
  account: '',
  phoneNumber: '',
  smsCode: ''
})

const validatePhone = (rule, value, callback) => {
  if (!value) {
    callback(new Error('请输入主体联系人电话'))
  } else if (!GLOBAL_PHONE_PATTERN.test(value)) {
    callback(new Error('请输入正确的主体联系人电话'))
  } else {
    callback()
  }
}

const forgotRules = {
  account: [
    { required: true, message: '请输入主体账号', trigger: 'blur' }
  ],
  phoneNumber: [
    { required: true, validator: validatePhone, trigger: 'blur' }
  ],
  smsCode: [
    { required: true, message: '请输入验证码', trigger: 'blur' },
    { len: 6, message: '验证码为6位数字', trigger: 'blur' }
  ]
}

const smsCodeButtonText = computed(() =>
  smsCodeCountdown.value > 0 ? `${smsCodeCountdown.value}秒后重试` : '发送验证码'
)

watch(
  () => props.modelValue,
  async value => {
    if (!value) {
      clearSmsCodeTimer()
      return
    }
    resetForm()
    forgotForm.account = props.initialAccount || ''
    await nextTick()
    forgotFormRef.value?.clearValidate()
  }
)

function resetForm() {
  forgotForm.account = ''
  forgotForm.phoneNumber = ''
  forgotForm.smsCode = ''
}

function clearSmsCodeTimer() {
  if (smsCodeTimer.value) {
    clearInterval(smsCodeTimer.value)
    smsCodeTimer.value = null
  }
  smsCodeCountdown.value = 0
}

function startSmsCodeCountdown() {
  clearSmsCodeTimer()
  smsCodeCountdown.value = COUNTDOWN_TIME
  smsCodeTimer.value = setInterval(() => {
    smsCodeCountdown.value -= 1
    if (smsCodeCountdown.value <= 0) {
      clearSmsCodeTimer()
    }
  }, 1000)
}

async function handleGetSmsCode() {
  if (smsCodeCountdown.value > 0 || smsCodeLoading.value) {
    return
  }
  try {
    await forgotFormRef.value?.validateField(['account', 'phoneNumber'])
  } catch {
    return
  }

  smsCodeLoading.value = true
  try {
    const response = await getSmsCaptcha(forgotForm.phoneNumber.trim())
    if (response && response.code === 200) {
      ElMessage.success('验证码已发送')
      startSmsCodeCountdown()
    }
  } catch (error) {
    console.error('获取找回密码验证码失败:', error)
  } finally {
    smsCodeLoading.value = false
  }
}

async function handleConfirmRetrieve() {
  try {
    await forgotFormRef.value?.validate()
  } catch {
    return
  }

  submitLoading.value = true
  try {
    const response = await retrieveEnterprisePassword(
      forgotForm.account.trim(),
      forgotForm.phoneNumber.trim(),
      forgotForm.smsCode.trim()
    )
    if (response && response.code === 200) {
      ElMessage.success(response.msg || '密码找回成功，新密码已发送至主体联系人邮箱')
      emit('success')
      visible.value = false
    }
  } catch (error) {
    console.error('找回密码失败:', error)
  } finally {
    submitLoading.value = false
  }
}

function handleClosed() {
  resetForm()
  clearSmsCodeTimer()
  forgotFormRef.value?.clearValidate()
}
</script>


<style lang="scss" scoped>
.forgot-password-form {
  min-height: 360px;
  padding: 12px 56px 20px;

  :deep(.el-form-item) {
    margin-bottom: 24px;

    .el-form-item__label {
      color: #000;
      font-weight: 500;
    }
  }

  .forgot-phone-input {
    height: 40px;

    :deep(.append-slot) {
      .sms-code-btn {
        height: 100%;
        padding: 0 16px;
        border: none;
        border-radius: 0;
        color: #206ef4;
        background: transparent;
        font-size: 14px;
        white-space: nowrap;

        &:hover:not(:disabled) {
          color: #004494;
          background: #f5f7fa;
        }

        &:disabled {
          color: #c0c4cc;
          background: transparent;
        }
      }
    }
  }

  .retrieve-password-tip {
    margin: 0 0 16px;
    font-size: 14px;
    line-height: 22px;
    color: #0d62f3;
    text-align: center;
  }

  .confirm-retrieve-btn {
    display: block;
    width: 180px;
    height: 40px;
    margin: 8px auto 0;
  }
}

@media (max-width: 768px) {
  .forgot-password-form {
    min-height: auto;
    padding: 4px 0 12px;

    .forgot-phone-input {
      :deep(.append-slot .sms-code-btn) {
        padding: 0 10px;
        font-size: 13px;
      }
    }
  }
}
</style>

<style lang="scss">
.enterprise-forgot-password-dialog {
  z-index: 3000 !important;

  .el-dialog__title {
    color: #000 !important;
    font-weight: 600 !important;
  }
}
</style>
